import type { ComponentType } from "react";

import { VerificationRequiredDialog } from "@layout/Topbar/VerificationRequiredDialog";
import { BlockUserDialog } from "@layout/UserList/BlockUserDialog";
import { MuteUserDialog } from "@layout/UserList/MuteUserDialog";

import { CustomStreamDialog } from "@screens/BroadcastSettings/components/CustomStreamDialog";
import { CustomDatesDialog } from "@screens/Earnings/components/CustomDatesDialog";
import { EditPostDialog } from "@screens/Posts/components/EditPostDialog";
import { PostSentToModerationDialog } from "@screens/Posts/components/PostSentToModerationDialog";
import { RejectedPostDialog } from "@screens/Posts/components/RejectedPostDialog";
import { DeleteRemarkDialog } from "@screens/Remarks/components/DeleteRemarkDialog";
import { UpdateRemarkDialog } from "@screens/Remarks/components/UpdateRemarkDialog";
import { FinishTourDialog } from "@screens/ShowSettings/components/FinishTourDialog";

import { DeleteGoalDialog } from "@features/goals/DeleteGoalDialog";
import { GoalSettingsDialog } from "@features/goals/GoalSettingsDialog";
import { MessageLinkDialog } from "@features/messenger/ChatContainer/MessageElement/MessageLinkDialog";
import { PermissionDisabledDialog } from "@features/permission/PermissionDisabledDialog";
import { PermissionEnabledDialog } from "@features/permission/PermissionEnabledDialog";

import type { DialogComponentPropsByKey, DialogKey } from "@storage/global/useDialogStore";
import { DIALOG_KEYS, useDialogStore } from "@storage/global/useDialogStore";

export { DIALOG_KEYS };

type ManagedDialogKey = Exclude<DialogKey, typeof DIALOG_KEYS.TIP_MENU_ACTIVITY>;
type ManagedDialogProps = DialogComponentPropsByKey[ManagedDialogKey];

const DIALOG_LOOKUP: Partial<Record<DialogKey, ComponentType<ManagedDialogProps>>> = {
	[DIALOG_KEYS.BLOCK_USER]: BlockUserDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.MUTE_USER]: MuteUserDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.MESSAGE_LINK]: MessageLinkDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.VERIFICATION_REQUIRED]: VerificationRequiredDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.PERMISSION_ENABLED]: PermissionEnabledDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.PERMISSION_DISABLED]: PermissionDisabledDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.UPDATE_REMARK]: UpdateRemarkDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.DELETE_REMARK]: DeleteRemarkDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.FINISH_TOUR]: FinishTourDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.CUSTOM_DATES]: CustomDatesDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.CUSTOM_STREAM]: CustomStreamDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.EDIT_POST]: EditPostDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.POST_SENT_TO_MODERATION]: PostSentToModerationDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.REJECTED_POST]: RejectedPostDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.GOAL_SETTINGS]: GoalSettingsDialog as ComponentType<ManagedDialogProps>,
	[DIALOG_KEYS.DELETE_GOAL]: DeleteGoalDialog as ComponentType<ManagedDialogProps>,
};

export const DialogManager = () => {
	const { dialogType, dialogProps, dialogId, isOpen, closeDialog } = useDialogStore();

	if (!isOpen || !dialogType) return null;

	const VisualDialog = DIALOG_LOOKUP[dialogType];
	const props = { ...dialogProps, onClose: () => closeDialog(dialogId) } as ManagedDialogProps;

	return VisualDialog ? <VisualDialog key={`${dialogType}-${dialogId}`} {...props} /> : null;
};
